import { RequireAccess } from '@/components'
import { WalletBalance } from '@/components/billing/WalletBalance'
import { useJobillaOrgId } from '@/hooks'
import { useAuth, usePrivateChannel } from '@/lib'
import { SettingsNav, SubtleLoader } from '@/ui'
import { ExclamationTriangleIcon, PlusIcon } from '@heroicons/react/24/outline'
import {
	broadcastChannel,
	customerWalletShowQueryKey,
	useCustomerValidateBillingDetails,
	useCustomerWalletShow,
	walletEvent,
} from '@jobilla/nexus-sdk'
import { useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { InventorySection } from './billing/InventorySection'

export function Inventory() {
	const { t, i18n } = useTranslation('common')
	const { can } = useAuth()
	const canAddItem = can('inventory:add-item')
	const organisationId = useJobillaOrgId()
	// Lets the header's "Add item" button open the create dialog owned by InventorySection.
	const openCreateRef = useRef<(() => void) | null>(null)

	const { data: billingValidation, isLoading: isBillingLoading } = useCustomerValidateBillingDetails(
		organisationId,
		undefined,
		{
			enabled: !!organisationId,
		},
	)
	const isBillingComplete = billingValidation?.validation.valid ?? false

	const wallet = useCustomerWalletShow(organisationId, {
		enabled: !!organisationId && can('billing:read'),
	})

	// Keep the balance live when the wallet is credited or debited elsewhere.
	usePrivateChannel(
		organisationId && can('billing:read')
			? broadcastChannel.OrganisationWallet({ organisation: organisationId })
			: null,
		{
			[walletEvent.Credited]: [customerWalletShowQueryKey],
			[walletEvent.Debited]: [customerWalletShowQueryKey],
			[walletEvent.Held]: [customerWalletShowQueryKey],
			[walletEvent.Settled]: [customerWalletShowQueryKey],
			[walletEvent.Released]: [customerWalletShowQueryKey],
		},
	)

	const currencyFormatter = new Intl.NumberFormat(i18n.language, {
		style: 'currency',
		currency: 'EUR',
	})

	return (
		<SettingsNav active="inventory" fullWidth={false}>
			<section className="space-y-8 mt-8 pr-8 max-w-[800px]">
				<div className="mt-10">
					<div className="flex items-end justify-between gap-4">
						<h2 className="text-2xl font-medium tracking-tight">
							{t('settings.billing.inventory')}
						</h2>
						{wallet.data && (
							<div className="text-right">
								<div className="text-[10px] font-medium text-grey-500 uppercase tracking-wider">
									{t('settings.wallet.currentBalance')}
								</div>
								<WalletBalance
									balanceCents={wallet.data.balance}
									taxPercentage={billingValidation?.customer?.tax?.taxRate.percentage ?? 0}
									currencyFormatter={currencyFormatter}
									className="text-2xl font-semibold text-grey-900 tabular-nums leading-tight"
								/>
							</div>
						)}
					</div>

					{can('inventory:view') && isBillingComplete && canAddItem
						? (
							<div className="mt-4 flex h-px items-center">
								<div className="h-px flex-1 bg-grey-100" />
								<button
									type="button"
									onClick={() => openCreateRef.current?.()}
									className="flex items-center gap-1.5 rounded-md border border-grey-200 bg-white px-2.5 py-1 text-xs font-medium text-grey-700 transition-colors hover:border-brand-500 hover:text-brand-700 cursor-pointer"
								>
									<PlusIcon className="h-3.5 w-3.5" />
									{t('inventory.addItem')}
								</button>
								<div className="h-px w-5 bg-grey-100" />
							</div>
						)
						: <div className="mt-4 h-px bg-grey-100" />}
				</div>

				<RequireAccess
					permission="inventory:view"
					fallback={
						<div className="text-sm text-gray-500">
							{t('settings.billing.noPermission')}
						</div>
					}
				>
					{isBillingLoading
						? <SubtleLoader delay={0} />
						: isBillingComplete
						? <InventorySection organisationId={organisationId} openCreateRef={openCreateRef} />
						: (
							<div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
								<div className="flex items-start gap-3">
									<ExclamationTriangleIcon className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
									<div>
										<div className="font-medium text-amber-900 mb-1">
											{t('settings.billing.billingRequired')}
										</div>
										<div className="text-sm text-amber-700">
											{t('settings.billing.billingRequiredInventory')}
										</div>
									</div>
								</div>
							</div>
						)}
				</RequireAccess>
			</section>
		</SettingsNav>
	)
}
